import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchBar from '@/components/SearchBar';
import { SearchResult, Video } from '@/types';
import { hybridVideoApi, hybridSearchApi } from '@/services/hybridApi'; 
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader, Filter, Clock, User, Package, Grid, List } from 'lucide-react';
import { formatDuration } from '@/lib/utils';
import SearchFilters from '@/components/SearchFilters';
import { useToast } from '@/components/ui/use-toast';
import SearchSkeleton from '@/components/SearchSkeleton';
import VideoSearchCard from '@/components/VideoSearchCard';
import SemanticResultCard from '@/components/SemanticResultCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import DocumentTitle from '@/components/DocumentTitle';
import { 
  Search as SearchIcon, 
  Play, 
  Users, 
  Tag, 
  Brain, 
  Sparkles,
  Download,
  Eye,
  Mic
} from 'lucide-react';
import { 
  videoSearchApi, 
  convertBackendSearchResultToFrontend,
  type SearchRequest,
  type ClipGenerationRequest
} from '@/services/videoSearchApi';

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session } = useAuth();
  const query = new URLSearchParams(location.search).get('q') || '';
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'relevance' | 'date' | 'duration' | 'title'>('relevance');
  const [activeFilters, setActiveFilters] = useState<{
    speakers: string[];
    objects: string[];
    duration: [number, number] | null;
    uploadDate: string;
    quality: string[];
  }>({
    speakers: [],
    objects: [],
    duration: null,
    uploadDate: 'any',
    quality: []
  });

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  
  // Search options
  const [searchOptions, setSearchOptions] = useState({
    searchType: 'text' as 'text' | 'visual' | 'timestamp' | 'objects' | 'speaker',
    topK: 10,
    useLLM: false,
    rerank: false,
    rerankMethod: 'openai' as 'openai' | 'cross_encoder',
    speakerFilter: '',
    objectFilter: [] as string[],
    timestamp: undefined as number | undefined,
  });

  // Clip generation options
  const [clipOptions, setClipOptions] = useState({
    padding: 2.0,
    smartMerge: true,
    mergeMode: 'hybrid' as 'loose' | 'strict' | 'llm' | 'hybrid',
  });

  // Fetch all user videos using hybrid API
  const { data: videos } = useQuery({
    queryKey: ['videos'],
    queryFn: () => hybridVideoApi.getVideos(session?.access_token)
  });

  // Fetch semantic search results for content within videos
  const { data: semanticResults, isLoading: isSemanticLoading } = useQuery({
    queryKey: ['semantic-search', query, activeFilters],
    queryFn: () => hybridSearchApi.semanticSearchAcrossVideos(query, activeFilters),
    enabled: !!query
  });

  // Get available videos
  const { data: backendVideos, isLoading: isLoadingVideos } = useQuery({
    queryKey: ['backend-videos'],
    queryFn: videoSearchApi.listVideos,
    retry: 3,
  });

  // Mutations
  const searchMutation = useMutation({
    mutationFn: async (request: SearchRequest) => {
      return videoSearchApi.search(request);
    },
    onSuccess: (data) => {
      const convertedResults = data.results.map(convertBackendSearchResultToFrontend);
      setSearchResults(convertedResults);
      toast({
        title: "Search completed",
        description: `Found ${data.total_results} results in ${data.search_time.toFixed(2)}s`,
      });
    },
    onError: (error) => {
      toast({
        title: "Search failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  });

  const clipGenerationMutation = useMutation({
    mutationFn: async (request: ClipGenerationRequest) => {
      return videoSearchApi.generateClip(request);
    },
    onSuccess: (data) => {
      toast({
        title: "Clip generated successfully!",
        description: `Duration: ${data.duration.toFixed(1)}s`,
      });
    },
    onError: (error) => {
      toast({
        title: "Clip generation failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  });

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim()) {
      toast({
        title: "Empty query",
        description: "Please enter a search query.",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    const request: SearchRequest = {
      query: searchQuery,
      video_id: selectedVideo || undefined,
      search_type: searchOptions.searchType,
      top_k: searchOptions.topK,
      speaker_filter: searchOptions.speakerFilter || undefined,
      object_filter: searchOptions.objectFilter.length > 0 ? searchOptions.objectFilter : undefined,
      timestamp: searchOptions.timestamp,
      use_llm: searchOptions.useLLM,
      rerank: searchOptions.rerank,
      rerank_method: searchOptions.rerankMethod,
    };

    searchMutation.mutate(request, {
      onSettled: () => setIsSearching(false)
    });
  }, [searchQuery, selectedVideo, searchOptions, searchMutation, toast]);

  const handleGenerateClip = useCallback((result: SearchResult) => {
    if (!selectedVideo) {
      toast({
        title: "No video selected",
        description: "Please select a video to generate clips from.",
        variant: "destructive"
      });
      return;
    }

    const request: ClipGenerationRequest = {
      video_id: selectedVideo,
      query: searchQuery,
      start_time: result.startTime,
      end_time: result.endTime,
      padding: clipOptions.padding,
      smart_merge: clipOptions.smartMerge,
      merge_mode: clipOptions.mergeMode,
    };

    clipGenerationMutation.mutate(request);
  }, [selectedVideo, searchQuery, clipOptions, clipGenerationMutation, toast]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter and sort videos based on query and filters
  const filteredVideos = videos?.filter(video => {
    if (!query) return true;
    const matchesQuery = video.title.toLowerCase().includes(query.toLowerCase()) || 
      video.metadata?.speakers.some(speaker => speaker.toLowerCase().includes(query.toLowerCase())) || 
      video.metadata?.objects.some(object => object.toLowerCase().includes(query.toLowerCase()));
    return matchesQuery;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'duration':
        return b.duration - a.duration;
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  }) || [];
  const filteredSemanticResults = semanticResults || [];

  return (
    <div className="min-h-screen bg-background">
      <DocumentTitle 
        title="Video Search - VideoBrain.ai | Advanced AI-Powered Video Content Discovery"
        description="Search through your video library with advanced AI capabilities. Find specific moments, speakers, objects, and content using natural language queries and semantic search technology."
      />
      
      <div className="container mx-auto px-4 py-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Video Search</h1>
          <p className="text-muted-foreground">
            Search through your video library using natural language, visual content, speakers, and objects
          </p>
        </div>

        <Tabs defaultValue="search" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="search">Search</TabsTrigger>
            <TabsTrigger value="advanced">Advanced Options</TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <SearchIcon className="h-5 w-5" />
                  Search Videos
                </CardTitle>
                <CardDescription>
                  Enter your search query and find relevant video segments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Search for anything in your videos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="text-lg"
                    />
                  </div>
                  <Button onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
                    {isSearching ? (
                      <SearchIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <SearchIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Search Type</Label>
                    <div className="flex gap-2 mt-2">
                      {['text', 'visual', 'timestamp', 'objects', 'speaker'].map((type) => (
                        <Button
                          key={type}
                          variant={searchOptions.searchType === type ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSearchOptions(prev => ({ ...prev, searchType: type as 'text' | 'visual' | 'timestamp' | 'objects' | 'speaker' }))}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Results Count</Label>
                    <Slider
                      min={1}
                      max={50}
                      step={1}
                      value={[searchOptions.topK]}
                      onValueChange={(values) => setSearchOptions(prev => ({ ...prev, topK: values[0] }))}
                      className="mt-2"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      {searchOptions.topK} results
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="use-llm"
                        checked={searchOptions.useLLM}
                        onCheckedChange={(checked) => setSearchOptions(prev => ({ ...prev, useLLM: checked }))}
                      />
                      <Label htmlFor="use-llm" className="flex items-center gap-1">
                        <Brain className="h-3 w-3" />
                        LLM Expansion
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="rerank"
                        checked={searchOptions.rerank}
                        onCheckedChange={(checked) => setSearchOptions(prev => ({ ...prev, rerank: checked }))}
                      />
                      <Label htmlFor="rerank" className="flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        Rerank Results
                      </Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {searchResults.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Search Results ({searchResults.length})</CardTitle>
                  <CardDescription>
                    Found {searchResults.length} relevant segments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {searchResults.map((result, index) => (
                      <SemanticResultCard
                        key={index}
                        result={result}
                        onGenerateClip={() => handleGenerateClip(result)}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {searchMutation.isError && (
              <Card className="border-destructive">
                <CardContent className="pt-6">
                  <p className="text-destructive">
                    Search failed. Please check if the backend is running and try again.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Advanced Search Options
                </CardTitle>
                <CardDescription>
                  Fine-tune your search with advanced filters and options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="speaker-filter">Speaker Filter</Label>
                    <Input
                      id="speaker-filter"
                      placeholder="Filter by speaker name"
                      value={searchOptions.speakerFilter}
                      onChange={(e) => setSearchOptions(prev => ({ ...prev, speakerFilter: e.target.value }))}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="timestamp">Timestamp (seconds)</Label>
                    <Input
                      id="timestamp"
                      type="number"
                      placeholder="Search around specific time"
                      value={searchOptions.timestamp || ''}
                      onChange={(e) => setSearchOptions(prev => ({ 
                        ...prev, 
                        timestamp: e.target.value ? parseFloat(e.target.value) : undefined 
                      }))}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label>Object Filter</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['person', 'car', 'laptop', 'phone', 'book', 'chair', 'table'].map((obj) => (
                      <Button
                        key={obj}
                        variant={searchOptions.objectFilter.includes(obj) ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          const newFilter = searchOptions.objectFilter.includes(obj)
                            ? searchOptions.objectFilter.filter(o => o !== obj)
                            : [...searchOptions.objectFilter, obj];
                          setSearchOptions(prev => ({ ...prev, objectFilter: newFilter }));
                        }}
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {obj}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Reranking Method</Label>
                  <div className="flex gap-2 mt-2">
                    {['openai', 'cross_encoder'].map((method) => (
                      <Button
                        key={method}
                        variant={searchOptions.rerankMethod === method ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSearchOptions(prev => ({ ...prev, rerankMethod: method as 'openai' | 'cross_encoder' }))}
                      >
                        {method === 'openai' ? 'OpenAI GPT' : 'Cross-Encoder'}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  Clip Generation Settings
                </CardTitle>
                <CardDescription>
                  Configure how video clips are generated from search results
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="padding">Time Padding (seconds)</Label>
                    <Slider
                      id="padding"
                      min={0}
                      max={10}
                      step={0.5}
                      value={[clipOptions.padding]}
                      onValueChange={(values) => setClipOptions(prev => ({ ...prev, padding: values[0] }))}
                      className="mt-2"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      {clipOptions.padding}s padding around segments
                    </p>
                  </div>

                  <div>
                    <Label>Merge Mode</Label>
                    <div className="flex gap-2 mt-2">
                      {['loose', 'strict', 'llm', 'hybrid'].map((mode) => (
                        <Button
                          key={mode}
                          variant={clipOptions.mergeMode === mode ? "default" : "outline"}
                          size="sm"
                          onClick={() => setClipOptions(prev => ({ ...prev, mergeMode: mode as 'loose' | 'strict' | 'llm' | 'hybrid' }))}
                        >
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="smart-merge"
                    checked={clipOptions.smartMerge}
                    onCheckedChange={(checked) => setClipOptions(prev => ({ ...prev, smartMerge: checked }))}
                  />
                  <Label htmlFor="smart-merge">Smart Segment Merging</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Search;
